import * as io from "io-ts";
import type { DurationUnit } from "luxon";
import { DateTime } from "luxon";
import React from "react";
import capitalize from "../../../lib/capitalize";
import createGlobalState from "@ui/lib/createGlobalState";
import { translationLocale } from "../../../i18n/locale";
import { nullable } from "diagnostics-lib/io";
import { fillPeriodGap } from "./utils/period";
import type { ReportingPeriod } from "../../../api/useReporting";

const usePeriodComparisonGlobalState = createGlobalState({
  storage: "session",
  key: "reportingPeriodComparison",
  type: io.strict({
    min: nullable(io.string),
    max: nullable(io.string),
  }),
  scoped: false,
});

export function usePeriodComparison(reportingPeriod: ReportingPeriod | null) {
  const defaultValue = React.useMemo(
    () =>
      reportingPeriod
        ? {
            min: DateTime.fromISO(reportingPeriod?.min).toFormat("yyyy-MM"),
            max: DateTime.fromISO(reportingPeriod?.max).toFormat("yyyy-MM"),
          }
        : { min: null, max: null },
    [reportingPeriod],
  );
  return usePeriodComparisonGlobalState({ defaultValue });
}

export function usePeriodComparisonOptions(reportingPeriod: ReportingPeriod | null) {
  return React.useMemo(() => {
    if (!reportingPeriod) return [];
    const fromDate = DateTime.fromISO(reportingPeriod.min);
    const toDate = DateTime.fromISO(reportingPeriod.max);

    return Array.from(fillPeriodGap(fromDate, toDate)).map(month => ({
      label: capitalize(month.setLocale(translationLocale).toFormat("MMMM yyyy")),
      value: month.toFormat("yyyy-MM"),
    }));
  }, [reportingPeriod]);
}

export function usePeriodComparisonDuration(
  reportingPeriod: ReportingPeriod | null,
  unit?: DurationUnit,
) {
  const [selectedPeriodComparison] = usePeriodComparison(reportingPeriod);
  return React.useMemo(() => {
    if (!selectedPeriodComparison?.min || !selectedPeriodComparison?.max) return null;
    const start = DateTime.fromISO(selectedPeriodComparison.min).startOf("month");
    const end = DateTime.fromISO(selectedPeriodComparison.max).endOf("month");
    return end.diff(start, unit || ["month", "year"]);
  }, [selectedPeriodComparison, unit]);
}
